import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { authService } from '@/services/authService';
import { handleApiError } from '@/services/api';
import type { LoginFormData, RegisterFormData } from '@/types';

export function useAuth() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { user, isAuthenticated, login, logout, setLoading } = useAuthStore();

  const currentUserQuery = useQuery({
    queryKey: ['currentUser'],
    queryFn: authService.getCurrentUser,
    enabled: isAuthenticated,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const loginMutation = useMutation({
    mutationFn: (data: LoginFormData) => authService.login(data),
    onSuccess: (data) => {
      console.log('[useAuth] onSuccess called with:', data);
      console.log('[useAuth] Calling login() to store token...');
      login(data.user, data.token);
      console.log('[useAuth] Token stored. localStorage:', localStorage.getItem('auth-storage'));
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      console.log('[useAuth] Navigating to /dashboard...');
      navigate('/dashboard');
    },
    onError: (error) => {
      console.log('[useAuth] onError called:', error);
      throw new Error(handleApiError(error));
    },
  });

  const registerMutation = useMutation({
    mutationFn: (data: RegisterFormData) => authService.register(data),
    onSuccess: (data) => {
      login(data.user, data.token);
      queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      navigate('/dashboard');
    },
    onError: (error) => {
      throw new Error(handleApiError(error));
    },
  });

  const logoutMutation = useMutation({
    mutationFn: authService.logout,
    onSettled: () => {
      logout();
      queryClient.clear();
      navigate('/login');
    },
  });

  const handleLogin = async (data: LoginFormData) => {
    setLoading(true);
    try {
      await loginMutation.mutateAsync(data);
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async (data: RegisterFormData) => {
    setLoading(true);
    try {
      await registerMutation.mutateAsync(data);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logoutMutation.mutate();
  };

  return {
    user: currentUserQuery.data ?? user,
    isAuthenticated,
    isLoading: loginMutation.isPending || registerMutation.isPending,
    error: loginMutation.error?.message || registerMutation.error?.message,
    login: handleLogin,
    register: handleRegister,
    logout: handleLogout,
  };
}
